/**
 * Frontend service for Zoho Books invoicing operations.
 * All calls go through Firebase Callable Functions – credentials are never
 * exposed to the browser.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, onSnapshot, Unsubscribe, query, orderBy, limit } from 'firebase/firestore';

const functions = getFunctions();

// Single cached callable — both list and detail go to the same warm container
const _zohoInvoicingFn = httpsCallable<
  { action: string } & Record<string, unknown>,
  { invoices?: ZohoInvoice[]; estimates?: ZohoEstimate[]; quotes?: ZohoEstimate[]; invoice?: ZohoInvoice }
>(functions, 'zohoInvoicing', { timeout: 15000 });

const _zohoSyncFn = httpsCallable<Record<string, unknown>, { synced: number }>(
  functions, 'zohoInvoiceSync', { timeout: 30000 });

export async function getInvoicesFromFirestore(): Promise<ZohoInvoice[]> {
  const result = await _zohoInvoicingFn({ action: 'listFromFirestore' });
  return result.data.invoices ?? [];
}

export async function syncInvoicesCache(): Promise<void> {
  await _zohoSyncFn({});
}

/**
 * Set up a real-time listener for invoices from Firestore.
 * This ensures UI updates automatically when invoice status changes.
 * @param onUpdate Callback function called whenever invoices change
 * @returns Unsubscribe function to stop listening
 */
export function subscribeToInvoicesFromFirestore(
  onUpdate: (invoices: ZohoInvoice[]) => void
): Unsubscribe {
  const db = getFirestore();
  const invoicesRef = collection(db, 'zoho', 'invoices', 'list');
  const q = query(invoicesRef, orderBy('_sortKey', 'desc'), limit(500));
  
  return onSnapshot(q, (snapshot) => {
    const invoices: ZohoInvoice[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      invoices.push({
        invoice_id: data.invoice_id || doc.id,
        invoice_number: data.invoice_number || '',
        customer_id: data.customer_id || '',
        customer_name: data.customer_name || '',
        status: data.status || '',
        date: data.date || '',
        total: data.total || 0,
        balance: data.balance || 0,
        ...data,
      } as ZohoInvoice);
    });
    onUpdate(invoices);
  }, (error) => {
    console.error('[subscribeToInvoicesFromFirestore] listener error:', error);
  });
}

export interface InvoiceCounts {
  total: number;
  paid: number;
  unpaid: number;
  overdue: number;
  draft: number;
  revenue: number | null;      // sales summary: excludes draft + void (matches Zoho)
  invoiceTotal: number | null; // gross total: all statuses
}

export async function getInvoiceCounts(params?: { date_start?: string; date_end?: string }): Promise<InvoiceCounts> {
  const fn = httpsCallable<Record<string, unknown>, InvoiceCounts>(getFunctions(), 'zohoInvoicing', { timeout: 15000 });
  const result = await fn({ action: 'counts', ...params });
  return result.data;
}

// ─── Types (mirrors functions/src/http/zohoInvoicing.ts) ─────────────────────

export interface ZohoLineItem {
  line_item_id?: string;
  item_id?: string;
  name?: string;
  description?: string;
  rate: number;
  quantity: number;
  item_total?: number;
  tax_name?: string;
  tax_percentage?: number;
  line_item_taxes?: Array<{ tax_name: string; tax_amount: number; tax_percentage: number; [key: string]: unknown }>;
  [key: string]: unknown;
}

export interface ZohoInvoice {
  invoice_id: string;
  invoice_number: string;
  // The 'create' action returns the raw Zoho response cast to this type regardless
  // of actual document type (invoice/estimate/salesorder) — for a Service Order the
  // real object only has these two fields populated, not invoice_id/invoice_number.
  salesorder_id?: string;
  salesorder_number?: string;
  customer_id: string;
  customer_name: string;
  customer_email?: string;
  status: string; // draft | sent | overdue | paid | void | unpaid | partially_paid | viewed
  date: string;          // YYYY-MM-DD — invoice issued date
  due_date?: string;     // YYYY-MM-DD
  // Present on estimate documents returned through shared invoice-like endpoints.
  estimate_number?: string;
  expiry_date?: string;
  currency_code?: string;
  reference_number?: string;
  total: number;
  balance: number;
  notes?: string;
  email?: string;
  billing_address?: {
    address?: string;
    street?: string;
    street2?: string;
    attention?: string;
    city?: string;
    state?: string;
    state_code?: string;
    zip?: string;
    country?: string;
    phone?: string;
  };
  is_emailed?: boolean;
  is_viewed_by_client?: boolean;
  client_viewed_time?: string;
  is_viewed_in_mail?: boolean;
  mail_first_viewed_time?: string;
  mail_last_viewed_time?: string;
  current_sub_status?: string;
  company_name?: string;
  company_address?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phone?: string;
    fax?: string;
  };
  phone?: string;
  website?: string;
  custom_fields?: Array<{
    field_id?: string;
    customfield_id?: string;
    label: string;
    value: string;
    data_type?: string;
    show_on_pdf?: boolean;
    [key: string]: unknown;
  }>;
  created_time?: string;
  last_modified_time?: string;
  line_items?: ZohoLineItem[];
  contact_persons_associated?: Array<{ contact_person_email?: string; [key: string]: unknown }>;
  sub_total?: number;
  discount_total?: number;
  discount?: number;
  discount_type?: string;
  shipping_charge?: number;
  adjustment?: number;
  adjustment_description?: string;
  tax_total?: number;
  tax_name?: string;
  tax_percentage?: number;
  is_inclusive_tax?: boolean;
  sub_total_inclusive_of_tax?: number;
  taxes?: Array<{ tax_name: string; tax_amount: number; tax_amount_formatted?: string; tax_percentage?: number; [key: string]: unknown }>;
  terms?: string;
  payment_made?: number;
  write_off_amount?: number;
  /** Top-level mirror written directly to the Firestore cache by the backend
   *  (cardPaymentValidate / helcimSettlementSync) so the flag shows even if Zoho
   *  doesn't webhook a custom-field-only change. */
  cf_payment_status?: string;
}

export interface ZohoEstimate extends ZohoInvoice {
  estimate_id: string;
  estimate_number: string;
  expiry_date?: string;
}

/**
 * True when an invoice carries the "ACH Pending" flag — set at ACH submit time
 * and cleared at batch settlement. Reads the top-level `cf_payment_status`
 * (direct cache write) or the `custom_fields` entry (from the Zoho webhook).
 * Paid/void invoices are never pending.
 */
export function isInvoiceAchPending(inv: ZohoInvoice): boolean {
  if (inv.status === 'paid' || inv.status === 'void') return false;
  if (String(inv.cf_payment_status ?? '').trim().toLowerCase() === 'ach pending') return true;
  const f = inv.custom_fields?.find(
    cf => cf.label === 'Payment Status'
      || (cf as Record<string, unknown>)['api_name'] === 'cf_payment_status'
  );
  return String(f?.value ?? '').trim().toLowerCase() === 'ach pending';
}

export interface ZohoGetInvoicesParams {
  page?: number;
  status?: string;        // 'paid' | 'unpaid' | 'overdue' | 'draft' | 'sent' | 'all'
  customer_id?: string;
  search_text?: string;
  salesorder_id?: string; // filter by Zoho SO relationship (immune to order-number field truncation)
  date_start?: string;    // YYYY-MM-DD
  date_end?: string;      // YYYY-MM-DD
}

// ─── API calls ────────────────────────────────────────────────────────────────

// What happened to the invoice emails (Zoho sends them through the Brevo relay, so Zoho can't see
// past the handoff). Looked up live from Brevo for the invoices on screen — nothing is stored.
export interface BrevoInvoiceEmailStatus {
  deliveredTime?: number;
  firstOpenedTime?: number;
  clickedTime?: number;
  proxyTime?: number;
  failedTime?: number;
  failedReason?: string;
}
const _brevoStatusFn = httpsCallable<
  {
    items?: Array<{ invoiceNumber: string; email: string; date?: string }>;
    receipts?: Array<{ key: string; email: string; time: number; windowMs?: number }>;
  },
  { statuses: Record<string, BrevoInvoiceEmailStatus>; receiptStatuses?: Record<string, BrevoInvoiceEmailStatus> }
>(functions, 'brevoInvoiceEmailStatus', { timeout: 30000 });

// "Payment Received" emails carry no invoice number, so they are matched to a payment by recipient + time.
export async function getBrevoReceiptStatuses(
  receipts: Array<{ key: string; email: string; time: number; windowMs?: number }>
): Promise<Record<string, BrevoInvoiceEmailStatus>> {
  const result = await _brevoStatusFn({ receipts });
  return result.data.receiptStatuses ?? {};
}

export async function getBrevoInvoiceEmailStatuses(
  items: Array<{ invoiceNumber: string; email: string; date?: string }>
): Promise<Record<string, BrevoInvoiceEmailStatus>> {
  const result = await _brevoStatusFn({ items });
  return result.data.statuses ?? {};
}

export async function getZohoInvoices(params?: ZohoGetInvoicesParams): Promise<ZohoInvoice[]> {
  const result = await _zohoInvoicingFn({ action: 'list', ...(params ?? {}) });
  return result.data.invoices ?? [];
}

export async function getZohoEstimates(params?: ZohoGetInvoicesParams): Promise<ZohoEstimate[]> {
  try {
    const result = await _zohoInvoicingFn({ action: 'listEstimates', ...(params ?? {}) });
    return result.data.estimates ?? [];
  } catch (error) {
    const message = String((error as { message?: string })?.message ?? '');
    if (!message.includes('Unknown action: listEstimates')) {
      throw error;
    }

    // Backward compatibility for older deployed zohoInvoicing handlers.
    const fallback = await _zohoInvoicingFn({ action: 'listQuotes', ...(params ?? {}) });
    return fallback.data.estimates ?? fallback.data.quotes ?? [];
  }
}

export interface ZohoSalesByItem {
  name: string;
  sales_amount?: number;
  item_total?: number;
  quantity_sold?: number | string;
  [key: string]: unknown;
}

export async function getZohoSalesByItem(params?: { date_start?: string; date_end?: string }): Promise<ZohoSalesByItem[]> {
  const result = await _zohoInvoicingFn({ action: 'salesByItem', ...(params ?? {}) });
  return ((result.data as unknown as { items?: ZohoSalesByItem[] }).items) ?? [];
}

/**
 * Net income for a date range, from Zoho's own Profit & Loss report — already
 * nets out voided invoices, refunds, and credit notes, unlike summing raw
 * invoice or payment totals ourselves. Returns null if Zoho's report shape
 * couldn't be confidently parsed (check Firebase function logs for the raw
 * response rather than trusting a guessed field path).
 */
export async function getZohoProfitAndLossIncome(params: { date_start: string; date_end: string }): Promise<number | null> {
  const result = await _zohoInvoicingFn({ action: 'profitAndLoss', ...params });
  const totalIncome = (result.data as unknown as { totalIncome?: number | null }).totalIncome;
  return typeof totalIncome === 'number' ? totalIncome : null;
}

/** Credit notes (not draft/void) dated in the range — Zoho's "Credit Note" figures. total includes tax. */
export async function getZohoCreditNotes(params: { date_start: string; date_end: string }): Promise<{ count: number; total: number; subTotal: number | null }> {
  const result = await _zohoInvoicingFn({ action: 'creditNotes', ...params });
  return (result.data as unknown as { creditNotes: { count: number; total: number; subTotal: number | null } }).creditNotes;
}

// The discount read can take a while on a first look at a long range, so it gets a longer timeout than the rest.
const _zohoDiscountsFn = httpsCallable<Record<string, unknown>, { discountTotal: number; remaining: number }>(
  functions, 'zohoInvoicing', { timeout: 60000 });

/**
 * Total discounts given on the invoices dated in the range (Zoho's "Discount Amount"). The server reads
 * each invoice once and caches it, so this asks repeatedly until everything has been read.
 */
export async function getZohoInvoiceDiscounts(
  params: { date_start: string; date_end: string }, onProgress?: (partial: number) => void,
): Promise<number> {
  let total = 0;
  for (let i = 0; i < 30; i++) {
    const result = await _zohoDiscountsFn({ action: 'invoiceDiscounts', ...params });
    total = result.data.discountTotal;
    onProgress?.(total);
    if (!result.data.remaining) break;
  }
  return total;
}

export async function getZohoInvoice(invoiceId: string): Promise<ZohoInvoice> {
  const result = await _zohoInvoicingFn({ action: 'detail', invoiceId });
  if (!result.data.invoice) throw new Error('No invoice returned');
  return result.data.invoice;
}

export async function getZohoEstimate(estimateId: string): Promise<ZohoEstimate> {
  try {
    const result = await _zohoInvoicingFn({ action: 'detailEstimate', invoiceId: estimateId });
    if (!result.data.invoice) throw new Error('No estimate returned');
    return result.data.invoice as ZohoEstimate;
  } catch (error) {
    const message = String((error as { message?: string })?.message ?? '');
    if (!message.includes('Unknown action: detailEstimate')) {
      throw error;
    }

    // Backward compatibility for older deployed zohoInvoicing handlers.
    // Fall back to list endpoints and resolve by estimate/invoice id.
    const byId = (item: ZohoEstimate) => {
      const itemEstimateId = String(item.estimate_id ?? '').trim();
      const itemInvoiceId = String(item.invoice_id ?? '').trim();
      return itemEstimateId === estimateId || itemInvoiceId === estimateId;
    };

    // First try the modern listEstimates action (usually returns the same list
    // the user clicked from), then degrade to listQuotes for older backends.
    try {
      for (let page = 1; page <= 10; page += 1) {
        const result = await _zohoInvoicingFn({ action: 'listEstimates', page });
        const estimates = (result.data.estimates ?? []) as ZohoEstimate[];
        const match = estimates.find(byId);
        if (match) return match;
        if (estimates.length < 25) break;
      }
    } catch (listErr) {
      const listMessage = String((listErr as { message?: string })?.message ?? '');
      if (!listMessage.includes('Unknown action: listEstimates')) {
        throw listErr;
      }
    }

    for (let page = 1; page <= 10; page += 1) {
      const fallback = await _zohoInvoicingFn({ action: 'listQuotes', page });
      const estimates = (fallback.data.estimates ?? fallback.data.quotes ?? []) as ZohoEstimate[];
      const match = estimates.find(byId);
      if (match) return match;
      if (estimates.length < 25) break;
    }

    throw new Error('Unable to load estimate detail from fallback list endpoint.');
  }
}

// ─── Billing summary (real revenue / receivables from Zoho) ─────────────────

export interface ZohoReceivableEntry {
  /** e.g. "Overdue", "Due Today", "Due Within 30 Days", "Due Later" */
  name?: string;
  /** raw total in account currency */
  total?: number;
  formatted_total?: string;
  invoice_count?: number;
  [key: string]: unknown;
}

export interface ZohoBillingSummary {
  /** Receivables grouped by aging bucket — from /reports/receivablesummary */
  receivableSummary: ZohoReceivableEntry[] | null;
  /** page_context from a 1-item invoice list — contains total_count etc. */
  pageContext: Record<string, unknown> | null;
}

export async function getZohoBillingSummary(): Promise<ZohoBillingSummary> {
  const fn = httpsCallable<Record<string, unknown>, ZohoBillingSummary>(
    getFunctions(), 'zohoInvoicing', { timeout: 15000 }
  );
  const result = await fn({ action: 'billingSummary' });
  return result.data;
}

// ─── Broad callable for create / update / recurring actions ─────────────────
const _zohoFn = httpsCallable<Record<string, unknown>, Record<string, unknown>>(
  functions, 'zohoInvoicing', { timeout: 20000 });

// ─── Zoho Contact (customer) type ────────────────────────────────────────────
export interface ZohoContact {
  contact_id: string;
  contact_name: string;
  company_name?: string;
  email?: string;
  phone?: string;
  billing_address?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phone?: string;
  };
  [key: string]: unknown;
}

// ─── CreateInvoice page operations ──────────────────────────────────────────

/** Alias used by CreateInvoice for invoice-number auto-generation */
export const getInvoices = getZohoInvoices;

/** Fetch the most recent quote for quote-number auto-generation */
export async function getQuotes(): Promise<Array<{ estimate_number: string }>> {
  const result = await _zohoInvoicingFn({ action: 'listQuotes' });
  return ((result.data as unknown as { quotes?: Array<{ estimate_number: string }> }).quotes) ?? [];
}

/** Fetch the most recent service order for SO-number auto-generation */
export async function getSalesOrders(): Promise<Array<{ salesorder_number: string }>> {
  const result = await _zohoInvoicingFn({ action: 'listServiceOrders' });
  return ((result.data as unknown as { salesorders?: Array<{ salesorder_number: string }> }).salesorders) ?? [];
}

/** Search Zoho contacts (customers) */
export async function getCustomers(searchText?: string): Promise<ZohoContact[]> {
  const result = await _zohoFn({ action: 'customers', searchText });
  return (result.data['contacts'] as ZohoContact[]) ?? [];
}

/** Create a Zoho Books invoice or quote */
export async function createInvoice(invoice: Record<string, unknown>, invoiceType?: string): Promise<ZohoInvoice> {
  const result = await _zohoFn({ action: 'create', invoice, ...(invoiceType ? { invoiceType } : {}) });
  if (!result.data['invoice']) throw new Error('No invoice returned from Zoho');
  return result.data['invoice'] as ZohoInvoice;
}

/** Update an existing Zoho Books invoice (mobile Invoice.tsx edit pencil). */
export async function updateInvoice(invoiceId: string, invoice: Record<string, unknown>, invoiceType?: string): Promise<ZohoInvoice> {
  const result = await _zohoFn({ action: 'update', invoiceId, invoice, ...(invoiceType ? { invoiceType } : {}) });
  if (!result.data['invoice']) throw new Error('No invoice returned from Zoho');
  return result.data['invoice'] as ZohoInvoice;
}

/**
 * Associate an invoice with the sales order it was converted from — Zoho's
 * own prerequisites (salesorder_number field set on the invoice, matching
 * customer/line item names) must already be true; this is the follow-up
 * call that actually completes the link and closes the sales order.
 * Non-fatal by design: returns { mapped: false, message } instead of
 * throwing, since the invoice itself already exists either way.
 */
export async function mapInvoiceWithOrder(invoiceId: string): Promise<{ mapped: boolean; message?: string }> {
  const result = await _zohoFn({ action: 'mapWithOrder', invoiceId });
  return result.data as { mapped: boolean; message?: string };
}

// ─── Email compose ("Save and Send", mirrors Zoho Books' own mobile app) ───────
// Field names below match Zoho's actual "Get invoice/sales order email content"
// response verbatim (confirmed against Zoho's own API docs) — notably, To/Cc
// recipients come as contact objects (with a `selected` default), not flat
// id arrays, and available email templates are bundled in this same response
// rather than needing a separate settings call.
export type ZohoEmailEntityType = 'salesorder' | 'invoice';

export interface ZohoEmailContact {
  contact_person_id?: number | string;
  first_name?: string;
  last_name?: string;
  email?: string | null;
  selected?: boolean;
  [key: string]: unknown;
}

export interface ZohoEmailFromAddress {
  user_name?: string;
  email?: string | null;
  organization_contact_id?: string;
  is_org_email_id?: boolean;
  selected?: boolean;
}

export interface ZohoEmailTemplateOption {
  email_template_id: string;
  name: string;
  selected?: boolean;
}

export interface ZohoEmailContent {
  subject: string;
  body: string;
  to_contacts?: ZohoEmailContact[];
  cc_mails_list?: ZohoEmailContact[];
  bcc_mails?: string[];
  from_emails?: ZohoEmailFromAddress[];
  emailtemplates?: ZohoEmailTemplateOption[];
  attach_pdf?: boolean;
  file_name?: string;
  [key: string]: unknown;
}

/** Prefilled subject/body/recipients/from-addresses/templates for the compose screen. */
export async function getEmailContent(
  entityType: ZohoEmailEntityType, entityId: string, templateId?: string,
): Promise<ZohoEmailContent> {
  const result = await _zohoFn({
    action: 'getEmailContent',
    entityType,
    ...(entityType === 'salesorder' ? { salesOrderId: entityId } : { invoiceId: entityId }),
    ...(templateId ? { templateId } : {}),
  });
  return result.data['emailContent'] as ZohoEmailContent;
}

/** Actually sends the sales order / invoice email via Zoho Books. */
export async function sendEntityEmail(
  entityType: ZohoEmailEntityType,
  entityId: string,
  params: {
    toMailIds: string[]; ccMailIds?: string[]; bccMailIds?: string[]; subject: string; body: string;
    sendCustomerStatement?: boolean;
    sendAttachment?: boolean;
    // Pick which of Zoho's configured sender addresses (from_emails) to send
    // from — either an org-wide address (sendFromOrgEmailId) or a specific
    // organization_contact_id.
    fromAddressId?: string;
    sendFromOrgEmailId?: boolean;
  },
): Promise<void> {
  await _zohoFn({
    action: 'sendEmail',
    entityType,
    ...(entityType === 'salesorder' ? { salesOrderId: entityId } : { invoiceId: entityId }),
    ...params,
  });
}

/** Look up payment status for a single booking ID */
export async function getBookingPaymentStatus(bookingId: string): Promise<{ paid: boolean; paymentStatus: string }> {
  const result = await _zohoFn({ action: 'bookingPaymentStatus', bookingId });
  return {
    paid: Boolean(result.data['paid']),
    paymentStatus: (result.data['paymentStatus'] as string) || 'unknown',
  };
}

/** Resolve the Zoho invoice ID linked to a booking (booking → SO → invoice). Null if none exists. */
export async function getZohoInvoiceIdForBooking(bookingId: string): Promise<string | null> {
  const result = await _zohoFn({ action: 'bookingPaymentStatus', bookingId });
  return (result.data['invoiceId'] as string | undefined) ?? null;
}

/** Batch payment status — one Firebase call for up to 30 booking IDs processed in parallel.
 *  Results are also written back to Firestore so subsequent loads read from the subscription. */
export async function getBookingPaymentStatusBatch(
  bookingIds: string[]
): Promise<Record<string, { paid: boolean; paymentStatus: string }>> {
  if (!bookingIds.length) return {};
  const result = await _zohoFn({ action: 'bookingPaymentStatusBatch', bookingIds });
  return (result.data['statuses'] as Record<string, { paid: boolean; paymentStatus: string }>) ?? {};
}

/** Update a Zoho contact's details */
export async function updateContact(contactId: string, contact: Record<string, unknown>): Promise<void> {
  await _zohoFn({ action: 'updateContact', contactId, contact });
}

/** Set up a recurring invoice in Zoho Books */
export async function makeZohoInvoiceRecurring(params: {
  customerId: string;
  recurrenceName: string;
  billingPeriod: 'daily' | 'weekly' | 'monthly' | 'yearly';
  billingPeriodIncrements: number;
  startDate: string;
  maxCycles: number;
  currency: string;
  lineItems: Array<{ description: string; rate: number; quantity: number }>;
}): Promise<void> {
  const { customerId, recurrenceName, billingPeriod, billingPeriodIncrements, startDate, maxCycles, currency, lineItems } = params;
  const freqMap: Record<string, string> = { daily: 'days', weekly: 'weeks', monthly: 'months', yearly: 'years' };
  const recurringInvoice: Record<string, unknown> = {
    customer_id: customerId,
    recurrence_name: recurrenceName,
    start_date: startDate,
    recurrence_frequency: freqMap[billingPeriod] ?? 'months',
    repeat_every: billingPeriodIncrements,
    currency_code: currency,
    line_items: lineItems.map(l => ({ description: l.description, rate: l.rate, quantity: l.quantity })),
  };
  if (maxCycles > 0) recurringInvoice['end_of_term'] = maxCycles;
  await _zohoFn({ action: 'createRecurring', recurringInvoice });
}
